import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  Camera as CamIcon,
  CheckCircle2,
  CircleHelp,
  CircleSlash,
  Loader2,
  OctagonAlert,
} from "lucide-react";
import {
  api,
  type MonitoringCamera,
  type MonitoringStatus,
  PipelineActivity,
  QuickStats,
} from "../api";
import { useRole } from "../hooks/useRole";
import { labelForHealthKey } from "../labels";

const GRAFANA_DASHBOARDS = [
  {
    id: "pipeline",
    label: "Pipeline & queues",
    url:
      "/grafana/d/blinkfree-redis-streams/blinkfree-redis-streams-and-pipeline" +
      "?theme=dark&kiosk&viewPanel=3&from=now-5m&to=now",
  },
  {
    id: "gpu",
    label: "GPU",
    url: "/grafana/d/blinkfree-gpu/blinkfree-gpu?theme=dark&kiosk",
  },
  {
    id: "containers",
    label: "Containers",
    url: "/grafana/d/blinkfree-containers/blinkfree-containers?theme=dark&kiosk",
  },
] as const;

type StatCard = {
  key: keyof QuickStats;
  label: string;
  unit?: string;
  fmt?: (v: number) => string;
  hint: string;
};

const THROUGHPUT_CARDS: StatCard[] = [
  {
    key: "ingestFps",
    label: "Crops/s",
    fmt: (v) => v.toFixed(2),
    hint: "Prometheus: sum(rate(blinkfree_items_out_total{stage=\"deepstream\"}[1m])). Person-crop messages published to Redis per second (zero when no detections pass the track sampler).",
  },
  {
    key: "activeTracks",
    label: "Tracklets",
    fmt: (v) => v.toFixed(0),
    hint: "Prometheus: sum(blinkfree_active_tracks{stage=\"deepstream\"}). People currently tracked across all cameras.",
  },
  {
    key: "captionFps",
    label: "Descriptions/s",
    fmt: (v) => v.toFixed(2),
    hint: "Prometheus: sum(rate(blinkfree_items_out_total{stage=\"captioner\"}[1m])). Crops described by vLLM per second.",
  },
  {
    key: "writeFps",
    label: "Saved/s",
    fmt: (v) => v.toFixed(2),
    hint: "Prometheus: sum(rate(blinkfree_items_out_total{stage=\"db_writer\"}[1m])). Records persisted to Postgres/Qdrant/MinIO per second.",
  },
  {
    key: "inFlightCaptioner",
    label: "In flight",
    fmt: (v) => v.toFixed(0),
    hint: "Prometheus: sum(blinkfree_in_flight{stage=\"captioner\"}). Messages read from Redis but not yet ACK'd by the captioner.",
  },
  {
    key: "captionerP95",
    label: "Describe p95",
    unit: "s",
    fmt: (v) => v.toFixed(2),
    hint: "Prometheus: histogram_quantile(0.95, rate(blinkfree_stage_latency_seconds_bucket{stage=\"captioner\"}[5m])). 95th percentile vLLM describe time.",
  },
  {
    key: "endToEndP95",
    label: "End-to-end p95",
    unit: "s",
    fmt: (v) => v.toFixed(1),
    hint: "Prometheus: histogram_quantile(0.95, rate(blinkfree_end_to_end_latency_seconds_bucket{stage=\"db_writer\"}[5m])). 95th percentile from frame capture to saved record.",
  },
  {
    key: "dropRate",
    label: "Drops",
    unit: "/s",
    fmt: (v) => v.toFixed(2),
    hint: "Prometheus: sum(rate(blinkfree_dropped_total[5m])). Messages dropped due to backpressure or missing artifacts, all stages.",
  },
  {
    key: "gpuUtil",
    label: "GPU usage",
    unit: "%",
    fmt: (v) => v.toFixed(0),
    hint: "Prometheus: avg(DCGM_FI_DEV_GPU_UTIL) from dcgm-exporter. Average SM utilization across GPUs (may read 0 during NVENC-only work).",
  },
];

const QUEUE_CARDS: StatCard[] = [
  {
    key: "cropsQueue",
    label: "Awaiting description",
    unit: "msg",
    fmt: (v) => v.toFixed(0),
    hint: "Crops not yet read by the captioner (crops stream / captioner-group lag). Zero when vLLM describe keeps up.",
  },
  {
    key: "tracksQueue",
    label: "Awaiting embedding",
    unit: "msg",
    fmt: (v) => v.toFixed(0),
    hint: "Captioned tracks not yet read by the embedder (tracks stream / embedder-group lag).",
  },
  {
    key: "toDbQueue",
    label: "Awaiting save",
    unit: "msg",
    fmt: (v) => v.toFixed(0),
    hint: "Embedded records not yet read by db_writer (to_db stream / db-writer-group lag) — waiting for Postgres, Qdrant, and MinIO.",
  },
  {
    key: "weaponVerifyQueue",
    label: "Awaiting weapon check",
    unit: "msg",
    fmt: (v) => v.toFixed(0),
    hint: "Suspected-weapon crops not yet read for the captioner’s priority second pass (weapon_verify / captioner-verify-group lag).",
  },
];

const BUFFER_CARDS: StatCard[] = [
  {
    key: "cropsBuffer",
    label: "Crops buffer",
    unit: "msg",
    fmt: (v) => v.toFixed(0),
    hint: "Redis XLEN on crops — retained stream entries (includes ACK'd history). Trimmed at crops_max_len (200k). High XLEN with zero queue lag means history, not unread work.",
  },
  {
    key: "tracksBuffer",
    label: "Tracks buffer",
    unit: "msg",
    fmt: (v) => v.toFixed(0),
    hint: "Redis XLEN on tracks — retained captioned messages (maxlen 200k).",
  },
  {
    key: "toDbBuffer",
    label: "Save buffer",
    unit: "msg",
    fmt: (v) => v.toFixed(0),
    hint: "Redis XLEN on to_db — retained records awaiting or past persistence (maxlen 200k).",
  },
  {
    key: "weaponVerifyBuffer",
    label: "Weapon buffer",
    unit: "msg",
    fmt: (v) => v.toFixed(0),
    hint: "Redis XLEN on weapon_verify — retained priority verify jobs.",
  },
  {
    key: "failedItemsBuffer",
    label: "DLQ buffer",
    unit: "msg",
    fmt: (v) => v.toFixed(0),
    hint: "Redis XLEN on failed_items — dead-letter queue for messages that failed processing (maxlen 50k).",
  },
];

const HEALTH_HINTS: Record<string, string> = {
  qdrant: "HTTP GET {qdrant}/readyz — vector search index readiness.",
  minio: "HTTP GET {minio}/minio/health/ready — object storage for crop photos.",
  vllm: "HTTP GET {vllm}/health — description engine (Qwen) readiness.",
  status: "Aggregate: ok when qdrant, minio, and vllm checks pass; degraded otherwise.",
};

function HintIcon({ hint }: { hint: string }) {
  return (
    <span title={hint} aria-label={hint} className="inline-flex">
      <CircleHelp
        size={14}
        className="shrink-0 text-bf-muted/70 hover:text-bf-muted"
      />
    </span>
  );
}

function StatTile({ card, value }: { card: StatCard; value: number | null }) {
  const display =
    value === null || value === undefined
      ? "--"
      : card.fmt
        ? card.fmt(value)
        : String(value);
  const dropsHot = card.key === "dropRate" && (value ?? 0) > 0;
  return (
    <div
      className="bf-card flex h-full min-w-0 flex-col overflow-hidden"
      title={card.hint}
    >
      <div className="flex min-h-[2.5rem] items-start justify-between gap-1.5">
        <div className="min-w-0 flex-1 text-xs leading-snug text-bf-muted line-clamp-2">
          {card.label}
        </div>
        <HintIcon hint={card.hint} />
      </div>
      <div
        className={
          "mt-auto flex min-h-[2.25rem] items-end gap-1 pt-2 " +
          (dropsHot ? "text-red-300" : "text-bf-text")
        }
      >
        <span className="min-w-0 truncate text-xl font-semibold leading-none tabular-nums sm:text-2xl">
          {display}
        </span>
        {card.unit && (
          <span className="shrink-0 pb-0.5 text-[10px] uppercase tracking-wide text-bf-muted sm:text-xs sm:normal-case sm:tracking-normal">
            {card.unit}
          </span>
        )}
      </div>
    </div>
  );
}

const CAMERA_STATE_STYLE: Record<string, string> = {
  streaming: "text-emerald-400",
  pending: "text-bf-muted",
  degraded: "text-amber-300",
  dead: "text-red-400",
  ended: "text-bf-violet",
};

function CamerasSection({
  cameras,
  status,
}: {
  cameras: MonitoringCamera[] | undefined;
  status: MonitoringStatus | undefined;
}) {
  if (!cameras || cameras.length === 0) return null;
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xs uppercase tracking-wide text-bf-muted">Cameras</h2>
        {/* Viewers and encoders are counted separately at the source: one
            person watching a 16-tile wall is one viewer holding sixteen
            encoders, so neither figure can be derived from the other. */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-bf-muted">
          {status?.viewers && (
            <span
              title={
                `${status.viewers.sessions} viewer page(s) open on ` +
                `${status.viewers.distinct_clients} device(s), holding ` +
                `${status.viewers.lease_holders} encoder seat(s).`
              }
            >
              Viewers: {status.viewers.sessions}
              {status.viewers.distinct_clients !== status.viewers.sessions &&
                ` (${status.viewers.distinct_clients} device${status.viewers.distinct_clients === 1 ? "" : "s"})`}
            </span>
          )}
          {status?.encoders && (
            <span title="On-demand preview encoder sessions in use vs the GPU's NVENC pool. A full pool means new preview tiles are refused until others close.">
              Preview encoders: {status.encoders.active}/{status.encoders.max}
            </span>
          )}
        </div>
      </div>
      <div className="bf-card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-bf-muted">
            <tr>
              <th className="p-3">Camera</th>
              <th className="p-3">Role</th>
              <th className="p-3">State</th>
              <th className="p-3">Last frame</th>
              <th className="p-3">Detail</th>
            </tr>
          </thead>
          <tbody>
            {cameras.map((c) => (
              <tr key={c.id} className="border-t border-white/5">
                <td className="p-3 font-medium">{c.name}</td>
                <td className="p-3 text-xs text-bf-muted">
                  {c.role === "view_only" ? "view only" : "detect & track"}
                </td>
                <td className={`p-3 text-xs ${CAMERA_STATE_STYLE[c.state] ?? "text-bf-muted"}`}>
                  {c.recovering ? "reconnecting…" : c.state}
                </td>
                <td className="p-3 text-xs tabular-nums text-bf-muted">
                  {c.seconds_since_frame == null
                    ? "—"
                    : c.seconds_since_frame < 5
                      ? "live"
                      : `${Math.round(c.seconds_since_frame)}s ago`}
                </td>
                <td className="p-3 text-xs text-bf-muted">{c.health_reason || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ActivityBanner({ activity }: { activity: PipelineActivity | undefined }) {
  if (!activity) {
    return (
      <div className="bf-card flex items-center gap-3 text-bf-muted">
        <Loader2 size={20} className="animate-spin" />
        <span>Checking pipeline status…</span>
      </div>
    );
  }
  const decodeFps = activity.decode_fps ?? activity.ingest_fps ?? 0;
  const cropsFps = activity.crops_fps ?? activity.ingest_fps ?? 0;
  let tone = "border-white/10";
  let icon = <CircleSlash size={20} className="text-bf-muted" />;
  let title = "Pipeline idle";
  let detail = "";
  let hint =
    "Streaming when sum(rate(blinkfree_deepstream_frame_lag_seconds_count[1m])) > 0.01 — i.e. decoded frames are reaching the tracker probe. Crop rate (items_out) is separate and can be zero when no people pass the sampling gate.";
  switch (activity.state) {
    case "streaming":
      tone = "border-emerald-500/30 bg-emerald-500/5";
      icon = <CheckCircle2 size={20} className="text-emerald-400" />;
      title = "Capturing video";
      detail = `Decoding ~${decodeFps.toFixed(0)} frames/sec from ${activity.cameras_enabled} camera${activity.cameras_enabled === 1 ? "" : "s"}.`;
      if (cropsFps <= 0.01) {
        detail +=
          " No crop messages in the last minute — decode is live but nothing is queued for description yet.";
      } else {
        detail += ` ~${cropsFps.toFixed(1)} crop${cropsFps === 1 ? "" : "s"}/sec queued for description.`;
      }
      break;
    case "idle":
      tone = "border-amber-500/30 bg-amber-500/5";
      icon = <OctagonAlert size={20} className="text-amber-400" />;
      title = "Pipeline idle";
      detail = `${activity.cameras_enabled} camera${activity.cameras_enabled === 1 ? "" : "s"} configured but no decoded frames are reaching the pipeline right now. Check that the cameras are reachable.`;
      break;
    case "no_cameras":
      tone = "border-white/10";
      icon = <CamIcon size={20} className="text-bf-muted" />;
      title = "No cameras configured";
      detail = "Add a camera on the Settings page to start capturing.";
      hint = "No enabled cameras in Postgres; ingest rate is not evaluated.";
      break;
    default:
      tone = "border-white/10";
      icon = <CircleSlash size={20} className="text-bf-muted" />;
      title = "Status unknown";
      detail = "Metrics service is unreachable.";
      hint = "Prometheus query for deepstream ingest rate failed or returned no data.";
  }
  return (
    <div className={`bf-card flex items-start gap-3 ${tone}`} title={hint}>
      <div className="mt-0.5">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="font-medium">{title}</div>
          <HintIcon hint={hint} />
        </div>
        <div className="text-sm text-bf-muted">{detail}</div>
      </div>
    </div>
  );
}

export default function Status({ embedded = false }: { embedded?: boolean } = {}) {
  const { role } = useRole();
  const [dashId, setDashId] = useState<(typeof GRAFANA_DASHBOARDS)[number]["id"]>("pipeline");
  const activeDash = GRAFANA_DASHBOARDS.find((d) => d.id === dashId) ?? GRAFANA_DASHBOARDS[0];

  const health = useQuery({
    queryKey: ["health"],
    queryFn: api.health,
    refetchInterval: 5_000,
  });

  const stats = useQuery({
    queryKey: ["quickstats"],
    queryFn: api.metrics.quickstats,
    refetchInterval: 5_000,
  });

  const activity = useQuery({
    queryKey: ["pipeline-activity"],
    queryFn: api.pipeline.activity,
    refetchInterval: 5_000,
  });

  // Unscoped on purpose: Status is a health view of the whole system, so it
  // reports every camera regardless of which store is being viewed.
  const monitoringCameras = useQuery({
    queryKey: ["monitoring", "cameras", "all"],
    queryFn: () => api.monitoring.cameras(),
    refetchInterval: 5_000,
  });
  const monitoringStatus = useQuery({
    queryKey: ["monitoring", "status"],
    queryFn: api.monitoring.status,
    refetchInterval: 5_000,
  });

  return (
    <div className="space-y-6">
      {!embedded && (
        <header>
          <h1 className="text-2xl font-bold tracking-tight text-white">Status</h1>
          <p className="text-sm text-bf-muted">
            Is the system capturing video and saving descriptions?
          </p>
        </header>
      )}

      <ActivityBanner activity={activity.data} />

      <CamerasSection
        cameras={monitoringCameras.data}
        status={monitoringStatus.data}
      />

      <section>
        <h2 className="mb-2 text-xs uppercase tracking-wide text-bf-muted">
          Live throughput
        </h2>
        <div className="grid auto-rows-fr grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {THROUGHPUT_CARDS.map((c) => (
            <StatTile
              key={c.key}
              card={c}
              value={stats.data ? stats.data[c.key] : null}
            />
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-1 text-xs uppercase tracking-wide text-bf-muted">
          Work waiting
        </h2>
        <p className="mb-2 text-xs text-bf-muted/80">
          Unread messages by next pipeline step (consumer-group lag).
        </p>
        <div className="grid auto-rows-fr grid-cols-2 gap-3 sm:grid-cols-4">
          {QUEUE_CARDS.map((c) => (
            <StatTile
              key={c.key}
              card={c}
              value={stats.data ? stats.data[c.key] : null}
            />
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-1 text-xs uppercase tracking-wide text-bf-muted">
          Stream buffers
        </h2>
        <p className="mb-2 text-xs text-bf-muted/80">
          Retained Redis entries (XLEN) — monitor capacity and history, not unread backlog.
        </p>
        <div className="grid auto-rows-fr grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {BUFFER_CARDS.map((c) => (
            <StatTile
              key={c.key}
              card={c}
              value={stats.data ? stats.data[c.key] : null}
            />
          ))}
        </div>
        {stats.isError && (
          <div className="mt-2 text-xs text-amber-300">
            metrics endpoint unreachable
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-xs uppercase tracking-wide text-bf-muted">
          Component health
        </h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {health.data &&
            Object.entries(health.data).map(([k, v]) => {
              const hint = HEALTH_HINTS[k] ?? "Dependency health check.";
              return (
                <div
                  key={k}
                  className="bf-card flex items-center justify-between gap-2"
                  title={hint}
                >
                  <span className="min-w-0 truncate">{labelForHealthKey(k, role)}</span>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <span
                      className={
                        typeof v === "boolean"
                          ? v
                            ? "text-emerald-400"
                            : "text-red-400"
                          : "text-bf-muted"
                      }
                    >
                      {typeof v === "boolean" ? (v ? "ok" : "down") : String(v)}
                    </span>
                    <HintIcon hint={hint} />
                  </div>
                </div>
              );
            })}
        </div>
      </section>

      <section>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xs uppercase tracking-wide text-bf-muted">
            Detailed dashboards
          </h2>
          <div className="flex flex-wrap gap-1">
            {GRAFANA_DASHBOARDS.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setDashId(d.id)}
                className={
                  "rounded px-2.5 py-1 text-xs transition-colors " +
                  (dashId === d.id
                    ? "bg-white/10 text-bf-text"
                    : "text-bf-muted hover:bg-white/5 hover:text-bf-text")
                }
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
        <div className="bf-card overflow-hidden p-0">
          <iframe
            title={`Grafana — ${activeDash.label}`}
            src={activeDash.url}
            className="h-[80vh] w-full border-0 bg-bf-bg"
          />
        </div>
      </section>
    </div>
  );
}
